import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Signalements.css';
import '../styles/responsive.css';
import { FiSearch, FiEye, FiCalendar } from 'react-icons/fi';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ResponsiveTable from '../components/ResponsiveTable';
import { useSignalements } from '../hooks/useSignalements';
import { useSearch } from '../contexts/SearchContext';
import { useServiceCategories } from '../hooks/useServices';

ChartJS.register(ArcElement, Tooltip, Legend);

const getStatusClass = (status) => {
  switch (status) {
    case 'Résolu': return 'status-green';
    case 'Fermé': return 'status-blue';
    case 'En cours': return 'status-orange';
    case 'Non traité': return 'status-red';
    case 'Sans suite': return 'status-gray';
    default: return '';
  }
};

const ReportsPage = () => {
  const navigate = useNavigate();
  const { globalSearch } = useSearch();
  const { categories: serviceCategories } = useServiceCategories();
  const [filters, setFilters] = useState({
    search: '',
    status: 'Tous',
    type: 'Type de service',
    month: 'Mois',
    date: ''
  });
  const [activeStatusFilter, setActiveStatusFilter] = useState('Tous');

  const {
    signalements,
    stats,
    frequentUsers,
    frequentPrestataires,
    loading,
    error,
    refetch,
    updateStatus
  } = useSignalements();

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    refetch(newFilters);
  };

  const handleStatusFilterClick = (status) => {
    setActiveStatusFilter(status);
    handleFilterChange('status', status);
  };

  const handleStatusAction = async (id, status) => {
    try {
      await updateStatus(id, status);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
    }
  };

  const handleViewDetails = (id) => {
    navigate(`/signalements/${id}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Filtrage des signalements avec recherche globale
  const filteredSignalements = signalements.filter(signalement => {
    // Filtrage par recherche globale (du header)
    if (globalSearch) {
      const searchLower = globalSearch.toLowerCase();
      return (
        signalement.id.toString().includes(searchLower) ||
        signalement.reported_by?.name?.toLowerCase().includes(searchLower) ||
        signalement.reported_against?.name?.toLowerCase().includes(searchLower) ||
        signalement.type?.toLowerCase().includes(searchLower) ||
        signalement.description?.toLowerCase().includes(searchLower) ||
        signalement.status?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  // Configuration pour le tableau responsive
  const columns = [
    {
      header: 'Id',
      accessor: 'id',
      render: (signalement) => `#${signalement.id}`
    },
    {
      header: 'De',
      accessor: 'reported_by',
      render: (signalement) => (
        <div className="with-avatar">
          <img src="/default-avatar.png" alt="avatar" className="avatar" />
          {signalement.reported_by.name}
        </div>
      )
    },
    {
      header: 'Signalé',
      accessor: 'reported_against',
      render: (signalement) => (
        <div className="with-avatar">
          <img src="/default-avatar.png" alt="avatar" className="avatar" />
          {signalement.reported_against.name}
        </div>
      )
    },
    {
      header: 'Type',
      accessor: 'type'
    },
    {
      header: 'Mission liée',
      accessor: 'mission_id',
      render: (signalement) => `#${signalement.mission_id}`
    },
    {
      header: 'Statut',
      accessor: 'status',
      render: (signalement) => (
        <span className={`status-tag ${getStatusClass(signalement.status)}`}>
          {signalement.status}
        </span>
      )
    },
    {
      header: 'Cible',
      accessor: 'target'
    },
    {
      header: 'Date',
      accessor: 'created_at',
      render: (signalement) => formatDate(signalement.created_at)
    }
  ];

  const mobileCardConfig = {
    title: (signalement) => `#${signalement.id}`,
    subtitle: (signalement) => signalement.type,
    fields: [
      { label: 'De', value: (signalement) => signalement.reported_by.name },
      { label: 'Signalé', value: (signalement) => signalement.reported_against.name },
      { label: 'Mission', value: (signalement) => `#${signalement.mission_id}` },
      { label: 'Statut', value: (signalement) => signalement.status },
      { label: 'Date', value: (signalement) => formatDate(signalement.created_at) }
    ],
    actions: [
      {
        label: 'Résoudre',
        onClick: (signalement) => handleStatusAction(signalement.id, 'Résolu'),
        className: 'action-blue'
      },
      {
        label: 'Clôturer',
        onClick: (signalement) => handleStatusAction(signalement.id, 'Fermé'),
        className: 'action-red'
      },
      {
        icon: <FiEye />,
        onClick: (signalement) => handleViewDetails(signalement.id),
        className: 'action-icon'
      }
    ]
  };

  const doughnutData = {
    labels: ['Traités', 'Non traités'],
    datasets: [
      {
        data: [stats.total - stats.non_traites, stats.non_traites],
        backgroundColor: ['#e6f0ff', '#0066ff'],
        borderWidth: 0,
      },
    ],
  };

  const doughnutOptions = {
    cutout: '70%',
    plugins: {
      legend: {
        display: false
      }
    },
    maintainAspectRatio: false
  };

  if (loading) {
    return <div className="loading">Chargement des signalements...</div>;
  }

  if (error) {
    return <div className="error">Erreur lors du chargement: {error}</div>;
  }

  return (
  <div className="reports-container">
    <h2>Signalements</h2>

    <div className="reports-top">
      <div className="reports-info-box">
        <div className="circle-container">
          <h4>Signalements non traités</h4>
          <div className="chart-container">
            <Doughnut data={doughnutData} options={doughnutOptions} />
            <div className="chart-center">
              <span className="chart-percentage">{stats.total > 0 ? Math.round((stats.non_traites / stats.total) * 100) : 0}%</span>
              <span className="chart-label">non traités</span>
            </div>
          </div>
        </div>
        <div>
          <h4>Signalement fréquents utilisateurs</h4>
          <ul>
            {frequentUsers.length > 0 ? (
              frequentUsers.map((user, index) => (
                <li key={index}>{user.name} ({user.report_count})</li>
              ))
            ) : (
              <li>Aucun utilisateur fréquent</li>
            )}
          </ul>
        </div>
        <div>
          <h4>Signalement fréquents prestataires</h4>
          <ul>
            {frequentPrestataires.length > 0 ? (
              frequentPrestataires.map((prestataire, index) => (
                <li key={index}>{prestataire.name} ({prestataire.report_count})</li>
              ))
            ) : (
              <li>Aucun prestataire fréquent</li>
            )}
          </ul>
        </div>
      </div>
    </div>

    <div className="reports-filters">
      <div className="filters-top-row">
        <select
          className="dropdown-service"
          value={filters.type}
          onChange={(e) => handleFilterChange('type', e.target.value)}
        >
          <option value="Type de service">Type de service</option>
          {serviceCategories && serviceCategories.length > 0 ? (
            serviceCategories.map((cat, idx) => (
              <option key={cat.id || idx} value={cat.name || cat}>{cat.name || cat}</option>
            ))
          ) : (
            <option value="" disabled>Chargement...</option>
          )}
        </select>
        <select 
          className="dropdown-month"
          value={filters.month || 'Mois'}
          onChange={(e) => handleFilterChange('month', e.target.value)}
        >
          <option value="Mois">Mois</option>
          <option value="Janvier">Janvier</option>
          <option value="Février">Février</option>
          <option value="Mars">Mars</option>
          <option value="Avril">Avril</option>
          <option value="Mai">Mai</option>
          <option value="Juin">Juin</option>
          <option value="Juillet">Juillet</option>
          <option value="Août">Août</option>
          <option value="Septembre">Septembre</option>
          <option value="Octobre">Octobre</option>
          <option value="Novembre">Novembre</option>
          <option value="Décembre">Décembre</option>
        </select>
      </div>
      
      <div className="filters-bottom-row">
        <div className="search-box">
          <FiSearch />
          <input 
            type="text" 
            placeholder="Recherche..." 
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </div>
        
        <div className="date-input">
          <FiCalendar />
          <input 
            type="date" 
            className="date-field"
            value={filters.date}
            onChange={(e) => handleFilterChange('date', e.target.value)}
          />
        </div>
        
        <div className="status-filters">
          {['Tous', 'Non traité', 'En cours', 'Résolu', 'Fermé', 'Sans suite'].map((label, i) => (
            <button 
              key={i} 
              className={`status-btn ${activeStatusFilter === label ? 'active' : ''}`}
              onClick={() => handleStatusFilterClick(label)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>

    <div className="table-container">
      <ResponsiveTable
        data={filteredSignalements}
        columns={columns}
        mobileCardConfig={mobileCardConfig}
        noDataMessage="Aucun signalement trouvé"
      />
    </div>
  </div>
  );
};

export default ReportsPage;
